<c-card class="broadcast-card shadow-sm border-0" *transloco="let t">
  <c-card-body>
    <div class="broadcast-page-header">
      <div class="broadcast-page-header__title">
        <span class="broadcast-page-header__icon">
          <i class="fa-solid fa-bullhorn"></i>
        </span>
        <div>
          <h1 class="broadcast-page-header__heading">{{ t('Team Broadcast') }}</h1>
          <p class="broadcast-page-header__subtitle">{{ t('Send push notifications to your workforce and track delivery, read, and acknowledgement status.') }}</p>
        </div>
      </div>
      <div class="broadcast-page-header__actions">
        <button cButton class="btn btn-ghost" title="{{ t('Refresh') }}" (click)="loadBroadcasts()">
          <i class="fa-solid fa-rotate-right me-1"></i> {{ t('Refresh') }}
        </button>
        <button cButton class="btn btn-create" (click)="createBroadcast()">
          <i class="fa-solid fa-plus me-1"></i> {{ t('Create Broadcast') }}
        </button>
      </div>
    </div>

    <form cForm class="broadcast-filter-bar" [formGroup]="formGroup" (ngSubmit)="updateFilter()">
      <c-row class="g-2 align-items-end">
        <c-col class="col-12 col-lg-3">
          <label cLabel class="broadcast-filter-bar__label">{{ t('Search') }}</label>
          <c-input-group>
            <span cInputGroupText><svg cIcon class="me-2" name="cil-search"></svg></span>
            <input cFormControl type="text" formControlName="search" placeholder="{{ t('Subject or message...') }}" />
          </c-input-group>
        </c-col>
        <c-col class="col-6 col-lg-2">
          <label cLabel class="broadcast-filter-bar__label">{{ t('From') }}</label>
          <input cFormControl type="date" formControlName="sent_from" />
        </c-col>
        <c-col class="col-6 col-lg-2">
          <label cLabel class="broadcast-filter-bar__label">{{ t('To') }}</label>
          <input cFormControl type="date" formControlName="sent_until" />
        </c-col>
        <c-col class="col-6 col-lg-2">
          <label cLabel class="broadcast-filter-bar__label">{{ t('Status') }}</label>
          <select cSelect formControlName="status">
            <option value="">{{ t('All') }}</option>
            <option value="draft">{{ t('Draft') }}</option>
            <option value="sent">{{ t('Sent') }}</option>
          </select>
        </c-col>
        <c-col class="col-6 col-lg-2">
          <label cLabel class="broadcast-filter-bar__label">{{ t('Priority') }}</label>
          <select cSelect formControlName="priority">
            <option value="">{{ t('All') }}</option>
            <option value="normal">{{ t('Normal') }}</option>
            <option value="critical">{{ t('Critical') }}</option>
          </select>
        </c-col>
        <c-col class="col-6 col-lg-auto">
          <button cButton class="btn btn-light" type="submit">
            <i class="fa-solid fa-magnifying-glass"></i>
            <span class="d-none d-lg-inline ms-1">{{ t('Search') }}</span>
          </button>
        </c-col>
      </c-row>
    </form>

    <div class="broadcast-table-wrap" *ngIf="broadcasts.length > 0">
      <table [hover]="true" [responsive]="true" align="middle" cTable class="broadcast-table mb-0">
        <thead>
          <tr>
            <th>{{ t('Subject') }}</th>
            <th class="text-nowrap">{{ t('Created') }}</th>
            <th>{{ t('Created By') }}</th>
            <th class="text-center">{{ t('Recipients') }}</th>
            <th style="min-width: 140px">{{ t('Read') }}</th>
            <th style="min-width: 140px">{{ t('Acknowledged') }}</th>
            <th>{{ t('Status') }}</th>
            <th>{{ t('Priority') }}</th>
            <th class="text-end">{{ t('Actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let broadcast of broadcasts">
            <td>
              <a class="broadcast-subject" href="#" (click)="$event.preventDefault(); viewBroadcast(broadcast)">
                <i class="fa-solid me-2 broadcast-subject__icon"
                   [ngClass]="broadcast.priority === 'critical' ? 'fa-triangle-exclamation text-danger' : 'fa-bullhorn'"></i>
                {{ broadcast.title }}
              </a>
            </td>
            <td class="text-nowrap text-muted">{{ broadcast.sent_at_local || broadcast.created_local || broadcast.created }}</td>
            <td>
              <span class="broadcast-sender">
                <span class="broadcast-avatar">{{ initials(broadcast.sender?.name) }}</span>
                <span class="broadcast-sender__name">{{ broadcast.sender?.name || broadcast.user_id }}</span>
              </span>
            </td>
            <td class="text-center fw-semibold">{{ broadcast.recipient_count }}</td>
            <td>
              <ng-container *ngIf="broadcast.status === 'sent'; else metricDash">
                <div class="broadcast-metric">
                  <span class="broadcast-metric__label">{{ broadcast.read_count }} ({{ broadcast.read_pct }}%)</span>
                  <c-progress class="broadcast-progress" thin>
                    <c-progress-bar [value]="broadcast.read_pct || 0" color="info-gradient"></c-progress-bar>
                  </c-progress>
                </div>
              </ng-container>
            </td>
            <td>
              <ng-container *ngIf="broadcast.status === 'sent'; else metricDash">
                <div class="broadcast-metric">
                  <span class="broadcast-metric__label">{{ broadcast.ack_count }} ({{ broadcast.ack_pct }}%)</span>
                  <c-progress class="broadcast-progress" thin>
                    <c-progress-bar [value]="broadcast.ack_pct || 0" color="success-gradient"></c-progress-bar>
                  </c-progress>
                </div>
              </ng-container>
            </td>
            <td>
              <span class="badge broadcast-badge" [ngClass]="statusBadgeClass(broadcast.status)">
                <i class="fa-solid me-1" [ngClass]="statusBadgeIcon(broadcast.status)"></i>{{ broadcast.status }}
              </span>
            </td>
            <td>
              <span class="badge broadcast-badge" [ngClass]="priorityBadgeClass(broadcast.priority)">{{ broadcast.priority }}</span>
            </td>
            <td class="text-nowrap text-end">
              <button cButton class="btn btn-sm btn-ghost me-1"
                      title="{{ broadcast.status === 'draft' ? t('Edit') : t('View') }}"
                      (click)="viewBroadcast(broadcast)">
                <i class="fa-solid" [ngClass]="broadcast.status === 'draft' ? 'fa-pen' : 'fa-eye'"></i>
              </button>
              <button cButton class="btn btn-sm btn-ghost me-1" *ngIf="broadcast.status === 'sent'"
                      title="{{ t('Export') }}" (click)="exportBroadcast(broadcast.id)">
                <i class="fa-solid fa-download"></i>
              </button>
              <button cButton class="btn btn-sm btn-ghost btn-ghost-danger" *ngIf="broadcast.status === 'draft'"
                      title="{{ t('Delete') }}" (click)="deleteBroadcast(broadcast)">
                <i class="fa-solid fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="broadcast-pagination" *ngIf="pagination.totalPages > 1">
      <c-input-group class="broadcast-pagination__group">
        <span cInputGroupText><i class="fa-solid fa-file-lines"></i></span>
        <select cSelect [(ngModel)]="pagination.page" [ngModelOptions]="{standalone: true}" (change)="changePage()">
          <option *ngFor="let p of pagination.pageArray" [value]="p">{{ t('Page') }} {{ p }}</option>
        </select>
      </c-input-group>
    </div>

    <div class="broadcast-empty" *ngIf="broadcasts.length === 0">
      <span class="broadcast-empty__icon"><i class="fa-solid fa-bullhorn"></i></span>
      <h5 class="broadcast-empty__title">{{ t('No broadcasts found.') }}</h5>
      <p class="broadcast-empty__text">{{ t('Create your first broadcast to send a push notification to your workforce.') }}</p>
      <button cButton class="btn btn-create" (click)="createBroadcast()">
        <i class="fa-solid fa-plus me-1"></i> {{ t('Create Broadcast') }}
      </button>
    </div>
  </c-card-body>
</c-card>

<ng-template #metricDash>
  <span class="text-muted">—</span>
</ng-template>
